// Atlas remediation R3 — best-effort channel-name tag parser (spec §5.1 filters).
//
// Tags (RAW / VIP / HD / FHD / 4K / UHD / 8K / HEVC / 60FPS / HDR / GOLD) are
// PROVIDER-applied suffixes parsed from the channel name. They are useful as a
// multi-select filter facet, but per the honesty doctrine (§2.1) they describe
// what the playlist *claims*, NOT measured quality — they are never rendered like
// detected-quality verdicts and never drive ranking. One canonical parser so the
// facet (and any future display) agree.

// Canonical tag → matcher. Order matters where one name could match several
// (8K before 4K; FHD before HD) so the strongest claim wins first; parseTags
// still returns every distinct match.
const TAG_PATTERNS: ReadonlyArray<readonly [string, RegExp]> = [
    ['8K', /\b8\s?k\b/i],
    ['4K', /\b(4\s?k|uhd)\b/i],
    ['FHD', /\b(fhd|1080p?)\b/i],
    ['HD', /\bhd\b/i],
    ['HEVC', /\b(hevc|h\.?265|x265)\b/i],
    ['60FPS', /\b(50|60)\s?fps\b/i],
    ['HDR', /\b(hdr|dolby\s?vision)\b/i],
    ['VIP', /\bvip\b/i],
    ['RAW', /\braw\b/i],
    ['GOLD', /\bgold\b/i],
    // Access / tier claims surfaced by this provider (measured in the DB):
    // PREMIUM, PPV (pay-per-view), SD (standard-def, the counterpart to HD).
    ['PREMIUM', /\bpremium\b/i],
    ['PPV', /\bppv\b/i],
    ['SD', /\bsd\b/i],
];

/** Every tag the parser can emit (stable order — used to seed the facet list). */
export const KNOWN_CHANNEL_TAGS: readonly string[] = TAG_PATTERNS.map(([t]) => t);

// Providers decorate tags with Unicode superscript / modifier letters instead of
// ASCII — e.g. ᴿᴬᵂ, ᴴᴰ, ᵁᴴᴰ, ᵃᵐᶻ, ⁴ᴷ. The TAG_PATTERNS regexes are ASCII, so we
// fold these glyphs to their ASCII equivalents before matching. (Measured on a
// real 55k playlist: 12,624 channels use ᴿᴬᵂ vs 37 with ASCII "RAW"; 5,352 use
// ᴴᴰ — so without this fold the Tags facet missed ~99% of RAW/HD channels.)
// Map is built from codepoints (not pasted glyphs) to avoid source-encoding drift.
const _SUP_PAIRS: ReadonlyArray<readonly [number, string]> = [
    // Modifier letter capitals (U+1D2C–U+1D42) + superscript capital V (U+2C7D).
    [0x1d2c, 'A'], [0x1d2e, 'B'], [0x1d30, 'D'], [0x1d31, 'E'], [0x1d33, 'G'], [0x1d34, 'H'],
    [0x1d35, 'I'], [0x1d36, 'J'], [0x1d37, 'K'], [0x1d38, 'L'], [0x1d39, 'M'], [0x1d3a, 'N'],
    [0x1d3c, 'O'], [0x1d3e, 'P'], [0x1d3f, 'R'], [0x1d40, 'T'], [0x1d41, 'U'], [0x1d42, 'W'],
    [0x2c7d, 'V'],
    // Superscript small letters.
    [0x1d43, 'a'], [0x1d47, 'b'], [0x1d9c, 'c'], [0x1d48, 'd'], [0x1d49, 'e'], [0x1da0, 'f'],
    [0x1d4d, 'g'], [0x02b0, 'h'], [0x2071, 'i'], [0x02b2, 'j'], [0x1d4f, 'k'], [0x02e1, 'l'],
    [0x1d50, 'm'], [0x207f, 'n'], [0x1d52, 'o'], [0x1d56, 'p'], [0x02b3, 'r'], [0x02e2, 's'],
    [0x1d57, 't'], [0x1d58, 'u'], [0x1d5b, 'v'], [0x02b7, 'w'], [0x02e3, 'x'], [0x02b8, 'y'],
    [0x1dbb, 'z'],
    // Superscript digits.
    [0x2070, '0'], [0x00b9, '1'], [0x00b2, '2'], [0x00b3, '3'], [0x2074, '4'], [0x2075, '5'],
    [0x2076, '6'], [0x2077, '7'], [0x2078, '8'], [0x2079, '9'],
];
const SUPERSCRIPT_FOLD: Record<string, string> = {};
for (const [cp, ascii] of _SUP_PAIRS) SUPERSCRIPT_FOLD[String.fromCodePoint(cp)] = ascii;
const SUPERSCRIPT_RE = new RegExp(`[${Object.keys(SUPERSCRIPT_FOLD).join('')}]`, 'gu');

/** Fold Unicode superscript / modifier-letter glyphs to ASCII. Pure. */
export function foldSuperscripts(s: string): string {
    if (typeof s !== 'string' || !s) return '';
    return s.replace(SUPERSCRIPT_RE, (ch) => SUPERSCRIPT_FOLD[ch] ?? ch);
}

// Pre-build the removal regex from the same key set as SUPERSCRIPT_FOLD so the
// two helpers are always in sync: if new glyphs are added to _SUP_PAIRS, they
// are automatically stripped here too. New instance (not an alias) to avoid
// sharing lastIndex on the global-flagged regex between foldSuperscripts and
// cleanChannelName.
const SUPERSCRIPT_STRIP_RE = new RegExp(`[${Object.keys(SUPERSCRIPT_FOLD).join('')}]`, 'gu');

/** Display-clean a channel name: remove provider superscript/modifier glyph runs
 *  (ᴿᴬᵂ, ᴴᴰ, ᵁᴴᴰ, ᵃᵐᶻ…) — they're surfaced as tag pills, so they're redundant
 *  noise in the name. Collapses the resulting whitespace. Falls back to the
 *  original (trimmed) name if stripping would empty it. Does NOT touch ASCII
 *  tag words (too risky to strip from names). */
export function cleanChannelName(name: string): string {
    if (typeof name !== 'string' || !name) return '';
    const stripped = name.replace(SUPERSCRIPT_STRIP_RE, ' ');
    const collapsed = stripped.replace(/\s{2,}/g, ' ').trim();
    return collapsed || name.trim();
}

// Provider "section divider" rows — decorative entries some feeds ship as if
// they were real channels, e.g. `##### UNITED KINGDOM #####` or
// `----- FRANCE -----`. They exist to visually separate a category's real
// channels and carry no tunable stream worth showing a Watch affordance for.
//
// Detected STRUCTURALLY, not by any label vocabulary: the (trimmed) name is
// wrapped on BOTH edges by a run of the SAME punctuation/symbol character,
// at least two characters long, each run immediately followed/preceded by
// whitespace (or the string boundary) rather than glued straight onto a
// letter. Both sides are required — not "starts OR ends with a run" — and
// the two runs must share the same character. A false positive here HIDES A
// REAL CHANNEL, which is far worse than leaving an actual divider row
// clickable, so the bar is the narrowest shape that still catches the whole
// measured population.
//
// Measured against `data/content.db`'s 55,780 distinct live-channel names
// (2026-08-16): 972 match, EVERY one hand-reviewed is a decorative divider
// (country names, section labels like "PPV"/"SPORT"/"KIDS", or bare runs of
// the separator itself) — zero look like a real channel. The run length
// varies 2–8 and the two edges are not always the same LENGTH (`#### DAZN
// MENA PPV #####` is 4-then-5) but are always the same CHARACTER, and always
// space-delimited from the label — both of those are exactly what the
// pattern below requires and nothing more. `Channel #1` does not match: `#1`
// is a single `#` immediately followed by a digit, never a repeated run, and
// there is no matching run at the other edge.
const SEPARATOR_RUN_CHARS = '#=\\-_*~+\u2013\u2014\u2500\u2550'; // # = - _ * ~ + – — ─ ═
const SEPARATOR_LEAD_RE = new RegExp(`^([${SEPARATOR_RUN_CHARS}])\\1+(?=\\s|$)`);
const SEPARATOR_TAIL_RE = new RegExp(`(?:^|\\s)([${SEPARATOR_RUN_CHARS}])\\1+$`);

/**
 * True when `name` is a decorative provider "section divider" row rather
 * than a real channel — see the block comment above for the exact shape and
 * the measured precision. Pure; safe to call per-render.
 */
export function isSeparatorChannelName(name: string): boolean {
    if (typeof name !== 'string') return false;
    const trimmed = name.trim();
    if (!trimmed) return false;
    const lead = trimmed.match(SEPARATOR_LEAD_RE);
    if (!lead) return false;
    const tail = trimmed.match(SEPARATOR_TAIL_RE);
    if (!tail) return false;
    return lead[1] === tail[1];
}

/** Extract the distinct provider tags claimed in a channel name. Best-effort. */
export function parseTags(name: string): string[] {
    if (typeof name !== 'string' || !name) return [];
    const folded = foldSuperscripts(name);
    const out: string[] = [];
    for (const [tag, re] of TAG_PATTERNS) {
        if (re.test(folded)) out.push(tag);
    }
    return out;
}
